// Taken (spec 4.5, golf 10 B51): one list Te doen with Nieuw (captured, not looked at yet), Ingepland per day and
// Zonder dag per kind; then Ooit and Klaar (logbook). Each task: a kind (category), a day, and a place.
import { useState } from "react";
import { TidyButton, ToReview } from "../components/AiReview";
import { CategoryFilter } from "../components/CategoryChoice";
import { useLiveQuery } from "dexie-react-hooks";
import { Tabs } from "radix-ui";
import { ChevronDown, ChevronRight, Clock, Flag } from "lucide-react";
import { groupTodo } from "@shared/todo";
import { addDays, displayDate, planningDate } from "@shared/time";
import { openTask } from "../components/TaskSheet";
import { SyncBadge } from "../components/SyncBadge";
import { cn } from "../components/ui";
import { db, type PlanEntry, type Task, type TaskCategory } from "../lib/db";
import { useCategories } from "../lib/categories";
import { useCollapsed } from "../lib/hooks";
import { keepTask } from "../lib/tasks";

type View = "todo" | "ooit" | "klaar";

const shortDate = new Intl.DateTimeFormat("nl-NL", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const short = (iso: string) => shortDate.format(new Date(`${iso}T00:00:00Z`));

function useTasks() {
  return useLiveQuery(async () => {
    const tasks = (await db.tasks.toArray()).filter((t) => !t.deleted_at);
    const planned = new Map<string, PlanEntry>();
    for (const e of await db.plan_entries.where("status").equals("planned").toArray()) {
      if (e.task_id && !e.deleted_at) planned.set(e.task_id, e);
    }
    // The block a planned task is in, for "Morgen, Eventueel klusje".
    const blockIds = [...planned.values()].map((e) => e.day_block_id).filter((id): id is string => !!id);
    const blocks = new Map((await db.day_blocks.bulkGet(blockIds)).filter((b) => !!b).map((b) => [b!.id, b!.title]));
    return { tasks, planned, blocks };
  });
}

function TaskRow({ task, extra }: { task: Task; extra?: string[] }) {
  const meta: { icon?: typeof Clock; text: string }[] = (extra ?? []).map((text) => ({ text }));
  if (task.due_on) meta.push({ icon: Flag, text: `Deadline ${short(task.due_on)}` });
  if (task.estimate_minutes) meta.push({ icon: Clock, text: task.estimate_minutes === 60 ? "1 uur" : `${task.estimate_minutes} min` });
  return (
    <button
      type="button"
      onClick={() => openTask(task.id)}
      className="flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 py-2 text-left hover:bg-surface"
    >
      <span className="min-w-0 flex-1">
        <span className={cn("block [overflow-wrap:anywhere]", task.status === "done" && "text-done line-through")}>{task.title}</span>
        {meta.length > 0 && (
          <span className="mt-0.5 flex flex-wrap gap-x-3 text-sm text-ink-soft">
            {meta.map(({ icon: Icon, text }) => (
              <span key={text} className="inline-flex items-center gap-1">
                {Icon && <Icon aria-hidden className="size-3.5" />}
                {text}
              </span>
            ))}
          </span>
        )}
      </span>
      <ChevronRight aria-hidden className="size-5 shrink-0 text-ink-soft" />
    </button>
  );
}

/** A new task: one tap on a kind (the AI's guess first) or Bewaren keeps it in Te doen (golf 10). */
function FreshRow({ task, categories }: { task: Task; categories: TaskCategory[] }) {
  const guess = task.ai_proposal?.category?.toLowerCase();
  const ordered = [...categories].sort((a, b) => Number(b.name.toLowerCase() === guess) - Number(a.name.toLowerCase() === guess));
  return (
    <li className="rounded-2xl border border-mist bg-surface pb-2">
      <TaskRow task={task} />
      <div role="group" aria-label={`Soort voor ${task.title}`} className="flex flex-wrap gap-2 px-3">
        {ordered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => void keepTask(task.id, c.id)}
            className={cn(
              "min-h-11 rounded-xl border-2 px-3 text-sm [overflow-wrap:anywhere]",
              c.name.toLowerCase() === guess ? "border-pine font-semibold text-pine" : "border-mist text-ink",
            )}
          >
            {c.name.toLowerCase() === guess ? (
              <>
                <span className="sr-only">{c.name}, voorstel</span>
                <span aria-hidden>{c.name}?</span>
              </>
            ) : (
              c.name
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void keepTask(task.id, null)}
          className="min-h-11 rounded-xl border-2 border-mist px-3 text-sm text-ink-soft"
        >
          Bewaren
        </button>
      </div>
    </li>
  );
}

/** A kind under Zonder dag, folded in or out per device. */
function KindGroup({ id, title, tasks }: { id: string; title: string; tasks: Task[] }) {
  const [folded, toggle] = useCollapsed(`todo:${id}`);
  return (
    <section aria-label={title} className="mb-2">
      <h3>
        <button
          type="button"
          aria-expanded={!folded}
          aria-controls={`soort-${id}`}
          onClick={toggle}
          className="flex min-h-11 w-full items-center justify-between gap-3 px-3 text-left font-semibold"
        >
          {title}
          <span className="flex items-center gap-2 text-sm font-normal text-ink-soft tabular">
            {tasks.length}
            <ChevronDown aria-hidden className={cn("size-5 transition-transform", !folded && "rotate-180")} />
          </span>
        </button>
      </h3>
      {!folded && (
        <ul id={`soort-${id}`}>
          {tasks.map((t) => (
            <li key={t.id}>
              <TaskRow task={t} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Empty({ children }: { children: string }) {
  return <p className="rounded-2xl border border-dashed border-mist p-4 text-ink-soft">{children}</p>;
}

export function InboxScreen() {
  const [view, setView] = useState<View>("todo");
  const [category, setCategory] = useState<string | null>(null);
  const data = useTasks();
  const categories = useCategories() ?? [];
  const today = planningDate(new Date());
  const tasks = data?.tasks ?? [];
  const planned = data?.planned ?? new Map<string, PlanEntry>();
  const byNewest = (a: Task, b: Task) => (a.created_at < b.created_at ? 1 : -1);
  const filter = category && categories.some((c) => c.id === category) ? category : null;
  const groups = groupTodo(tasks, planned, categories, filter);
  // The number on the tab: everything new, with Te controleren, whatever the filter (L141).
  const freshCount = tasks.filter((t) => t.status === "inbox" && !planned.has(t.id)).length;
  const reviewCount = tasks.filter((t) => t.status === "inbox" && t.ai_state === "review").length;
  const kindOf = (t: Task) => categories.find((c) => c.id === t.category_id)?.name;
  const dayTitle = (date: string) =>
    date === today
      ? "Vandaag"
      : date === addDays(today, 1)
        ? "Morgen"
        : date < today
          ? `${displayDate(date)}, nog open`
          : displayDate(date);

  // Ooit and Klaar: the filter holds for Ooit; the Klaar logbook shows everything.
  const someday = tasks.filter((t) => t.status === "someday" && (!filter || t.category_id === filter)).sort(byNewest);
  const done = tasks
    .filter((t) => t.status === "done" && t.completed_at)
    .sort((a, b) => (a.completed_at! < b.completed_at! ? 1 : -1))
    .slice(0, 200);
  const doneByDay = new Map<string, Task[]>();
  for (const t of done) {
    // The planning day (switches at 04:00, L18): a check at 01:00 belongs to the evening before.
    const day = planningDate(new Date(t.completed_at!));
    doneByDay.set(day, [...(doneByDay.get(day) ?? []), t]);
  }
  const nothing = !groups.fresh.length && !groups.planned.length && !groups.undated.length && !reviewCount;

  const tab = (value: View, label: string, count?: number) => (
    <Tabs.Trigger
      value={value}
      className="min-h-12 flex-1 rounded-xl px-2 font-semibold text-ink-soft data-[state=active]:bg-pine data-[state=active]:text-paper"
    >
      {label}
      {count ? <span className="ml-1 tabular">({count})</span> : null}
    </Tabs.Trigger>
  );

  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <h1 className="text-[1.75rem] font-bold">Taken</h1>
        <SyncBadge />
      </header>
      <Tabs.Root value={view} onValueChange={(v) => setView(v as View)}>
        <Tabs.List aria-label="Weergave" className="mb-4 flex gap-1 rounded-2xl border border-mist bg-surface p-1">
          {tab("todo", "Te doen", freshCount)}
          {tab("ooit", "Ooit")}
          {tab("klaar", "Klaar")}
        </Tabs.List>
        {view !== "klaar" && <CategoryFilter value={filter} onChange={setCategory} />}

        <Tabs.Content value="todo">
          <ToReview />
          {groups.fresh.length > 0 && (
            <section aria-labelledby="nieuw" className="mb-6">
              <h2 id="nieuw" className="mb-1 px-1 text-lg font-semibold">
                Nieuw <span className="text-base font-normal text-ink-soft tabular">({groups.fresh.length})</span>
              </h2>
              <p className="mb-2 px-1 text-sm text-ink-soft">Kies een soort, plan het in, of tik op Bewaren.</p>
              <TidyButton count={groups.fresh.length} />
              <ul className="flex flex-col gap-2">
                {groups.fresh.map((t) => (
                  <FreshRow key={t.id} task={t} categories={categories} />
                ))}
              </ul>
            </section>
          )}

          {groups.planned.length > 0 && (
            <section aria-labelledby="ingepland" className="mb-6">
              <h2 id="ingepland" className="mb-1 px-1 text-lg font-semibold">
                Ingepland
              </h2>
              {groups.planned.map((d) => (
                <section key={d.date} aria-label={dayTitle(d.date)} className="mb-2">
                  <h3 className="px-3 pt-1 text-sm font-semibold text-ink-soft first-letter:uppercase">{dayTitle(d.date)}</h3>
                  <ul>
                    {d.tasks.map((t) => {
                      const block = planned.get(t.id)?.day_block_id;
                      const extra = [data?.blocks.get(block ?? ""), kindOf(t)].filter((x): x is string => !!x);
                      return (
                        <li key={t.id}>
                          <TaskRow task={t} extra={extra} />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </section>
          )}

          {groups.undated.length > 0 && (
            <section aria-labelledby="zonder-dag" className="mb-6">
              <h2 id="zonder-dag" className="mb-1 px-1 text-lg font-semibold">
                Zonder dag
              </h2>
              {groups.undated.map((g) => (
                <KindGroup
                  key={g.category?.id ?? "overig"}
                  id={g.category?.id ?? "overig"}
                  title={g.category?.name ?? "Overig"}
                  tasks={g.tasks}
                />
              ))}
            </section>
          )}

          {data && nothing && (
            <Empty>{filter ? "Niets te doen in deze soort." : "Niets te doen. Met de plusknop zet je hier iets in."}</Empty>
          )}
        </Tabs.Content>

        <Tabs.Content value="ooit">
          <p className="mb-2 px-3 text-ink-soft">Ideeën en wat ooit kan. Uit zicht, niet weg. Nieuw komt hier na 30 dagen vanzelf.</p>
          {someday.length ? (
            <ul>
              {someday.map((t) => (
                <li key={t.id}>
                  <TaskRow task={t} />
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Niets in Ooit.</Empty>
          )}
        </Tabs.Content>

        <Tabs.Content value="klaar">
          {doneByDay.size ? (
            [...doneByDay].map(([day, list]) => (
              <section key={day} className="mb-4">
                <h2 className="mb-1 px-3 font-semibold">{displayDate(day)}</h2>
                <ul>
                  {list.map((t) => (
                    <li key={t.id}>
                      <TaskRow task={t} />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <Empty>Wat je afvinkt, zie je hier terug.</Empty>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </main>
  );
}
